/**
 * admin/query-keys — TanStack Query key factory for the back-office panels.
 *
 * Same contract as `utils/query-keys.ts`: never inline string arrays at the call
 * site, always go through this factory so invalidation prefixes stay consistent
 * across the admin features (users, audit, integrations, flags, metrics).
 */

export interface AdminUsersFilter {
  q?: string;
  role?: string;
  page?: number;
}

export interface AdminAuditFilter {
  entityType?: string;
  action?: string;
  userId?: string;
  page?: number;
}

/** Filter for the feature catalog (`admin.features`). */
export interface AdminFeaturesFilter {
  search?: string;
  category?: string;
  scope?: string;
  status?: string;
}

export const adminQueryKeys = {
  all: ["admin"] as const,
  users: (filter: AdminUsersFilter = {}) => ["admin", "users", filter] as const,
  audit: (filter: AdminAuditFilter = {}) => ["admin", "audit", filter] as const,
  // F2-D: integrations resolve by scope (global if `orgId` is absent).
  integrations: (orgId?: string | null) => ["admin", "integrations", orgId ?? "global"] as const,
  // F2-D: self-describing feature catalog.
  features: (filter: AdminFeaturesFilter = {}) => ["admin", "features", filter] as const,
  flags: () => ["admin", "flags"] as const,
  maintenance: () => ["admin", "maintenance"] as const,
  metrics: () => ["admin", "metrics"] as const,
  // F3-E: media management (videos + usage per org).
  mediaVideos: (filter: { filter?: string; org?: string } = {}) =>
    ["admin", "media", "videos", filter] as const,
  mediaUsage: () => ["admin", "media", "usage"] as const,
  // CONFIG_OVER_CODE: operational platform configuration.
  appConfig: () => ["admin", "config"] as const,
  // CONFIG_OVER_CODE: config for ONE academy (org level).
  orgConfig: (orgId?: string | null) => ["admin", "config", "org", orgId ?? "self"] as const,
};
